import { useMemo, useState, type ReactNode } from 'react';

import { EmptyState, ErrorState, Spinner } from '@/components/ui';
import { cx } from '@/components/ui/classNames';

/**
 * Liste links, Inhalt rechts (REQ-25 §4.3) — die Gestalt, die die Abschnitte der
 * Administration teilen. Vorlage ist `MasterDetail.tsx` der ChinaApp; abgelöst wird
 * `AdminMasterDetail`.
 */
export interface MasterItem<TId extends string | number = number> {
  id: TId;
  label: string;
  /** Abweichender Suchtext, etwa Name und E-Mail. */
  searchText?: string;
  /** Zweite Zeile in `--text-2`, z. B. die E-Mail. */
  description?: string;
  /** Rechts im Eintrag, etwa ein Chip oder eine Anzahl. */
  meta?: ReactNode;
}

export interface MasterDetailProps<TId extends string | number = number> {
  listLabel: string;
  items: MasterItem<TId>[];
  selectedId: TId | null;
  onSelect: (id: TId) => void;
  loading: boolean;
  /** `null` oder `undefined`, solange kein Fehler vorliegt. */
  error: unknown;
  onRetry: () => void;
  /** Ohne Angabe keine Suchzeile. */
  searchPlaceholder?: string;
  /** Zusätzliches Bedienelement über der Liste, etwa ein Filter. */
  filter?: ReactNode;
  /** Unter der Liste, meist „New". */
  toolbar?: ReactNode;
  emptyText: string;
  /** Breitere Liste für lange Namen. */
  wide?: boolean;
  children: ReactNode;
}

/** Technische Einzelheit für `ErrorState` — die Meldung des Servers, sofern es eine gibt. */
function describeError(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : undefined;
}

/** „{n} Einträge" bzw. „{n} von {m} Einträgen"; bei genau einem Eintrag in der Einzahl. */
function countText(visible: number, total: number): string {
  if (visible === total) return total === 1 ? '1 Eintrag' : `${String(total)} Einträge`;
  return `${String(visible)} von ${String(total)} ${total === 1 ? 'Eintrag' : 'Einträgen'}`;
}

/**
 * Die Suche filtert im Client: Die Listen der Administration kommen vollständig vom
 * Server, eine Seitenaufteilung gibt es dort nicht.
 */
export function MasterDetail<TId extends string | number = number>(props: MasterDetailProps<TId>) {
  const { items, onSelect, selectedId } = props;
  const [search, setSearch] = useState('');
  const failed = props.error !== null && props.error !== undefined;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return items;
    return items.filter((item) => (item.searchText ?? item.label).toLowerCase().includes(needle));
  }, [items, search]);

  const renderList = (): ReactNode => {
    if (props.loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Wird geladen …
        </div>
      );
    }
    if (failed) {
      return (
        <ErrorState
          size="sm"
          message="Die Liste konnte nicht geladen werden."
          detail={describeError(props.error)}
          onRetry={props.onRetry}
        />
      );
    }
    if (visible.length === 0) {
      return <EmptyState size="sm" title={items.length === 0 ? props.emptyText : 'Kein Treffer'} />;
    }
    return (
      <ul className="iwm-admin-master-list" aria-label={props.listLabel}>
        {visible.map((item) => {
          const active = item.id === selectedId;
          return (
            <li key={item.id}>
              <button
                type="button"
                aria-current={active ? 'true' : undefined}
                className={cx('iwm-admin-master-item', active && 'iwm-admin-master-item--active')}
                onClick={() => {
                  onSelect(item.id);
                }}
              >
                {/* Die Listenspalte ist schmal; neben einer Marke bleibt fuer lange
                    Namen wenig Platz. Der Kurzhinweis traegt den vollen Text. */}
                <span className="iwm-admin-master-item-text">
                  <span className="iwm-admin-master-item-label" title={item.label}>
                    {item.label}
                  </span>
                  {item.description === undefined ? null : (
                    <span className="iwm-admin-master-item-description" title={item.description}>
                      {item.description}
                    </span>
                  )}
                </span>
                {item.meta === undefined || item.meta === null ? null : (
                  <span className="iwm-admin-master-item-meta">{item.meta}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={cx('iwm-admin-master', props.wide === true && 'iwm-admin-master--wide')}>
      <div className="iwm-admin-master-pane">
        {props.searchPlaceholder === undefined ? null : (
          <span className="e-input-group iwm-admin-search">
            <input
              type="search"
              className="e-input"
              placeholder={props.searchPlaceholder}
              aria-label={`${props.listLabel} durchsuchen`}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            <span className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass" />
          </span>
        )}
        {props.filter}
        {renderList()}
        {props.loading || failed || items.length === 0 ? null : (
          <span className="iwm-admin-master-count">{countText(visible.length, items.length)}</span>
        )}
        {props.toolbar === undefined ? null : (
          <div className="iwm-admin-master-toolbar">{props.toolbar}</div>
        )}
      </div>
      <div className="iwm-admin-master-detail">{props.children}</div>
    </div>
  );
}

export default MasterDetail;
